'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    IconButton,
    Link,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../redux/root-reducer';
import type {
    IApplicationReviewPlatformStats,
    IInfluencerReviewSnapshot,
} from '../../../redux/buzzbridge/campaignApplicationReview.types';
import type { ICampaignParticipantDetail } from '../../../redux/buzzbridge/types';
import {
    approveCampaignJoinRequest,
    cancelCampaignInvite,
    clearCampaignApplicationReview,
    getCampaignApplicationReview,
    getOffsiteCampaignDetails,
    rejectCampaignJoinRequest,
} from '../../../redux/buzzbridge/actions';
import {
    campaignApplicationReviewErrorSelector,
    campaignApplicationReviewSelector,
    cancellingInviteSelector,
    gettingCampaignApplicationReviewSelector,
} from '../../../redux/buzzbridge/selectors';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';
import { formatSocialHandle } from '../../../utils/socialHandle';
import { influencerProfileAvatarSx } from '../../../components/BuzzBridge/utils/creatorProfileHelpers';
import {
    InstagramGlyph,
    PLATFORM_BRAND,
    TiktokGlyph,
    YoutubeGlyph,
} from '../../../components/BuzzBridge/SocialPlatformSvgs';

const ON_VARIANT = 'rgba(255,255,255,0.55)';
const QUICK_REASONS = [
    'Aesthetic Mismatch',
    'Budget Constraint',
    'Content Style',
    'Audience Location',
    'Niche Overlap',
] as const;

function tagLabel(tag: string | { main: string; sub?: string }): string {
    if (typeof tag === 'string') return tag;
    return tag.sub ? `${tag.main} · ${tag.sub}` : tag.main;
}

function formatFollowers(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n ?? 0);
}

function primaryHandleFromInfluencer(infl?: IInfluencerReviewSnapshot): string | undefined {
    if (!infl) return undefined;
    const platforms = [infl.instagram, infl.tiktok, infl.youtube, infl.twitter, infl.facebook];
    for (const p of platforms) {
        if (p?.username) return formatSocialHandle(p.username);
    }
    for (const p of platforms) {
        if (p?.profile_link) return p.profile_link;
    }
    return undefined;
}

function platformProfileUrl(
    platform: 'instagram' | 'tiktok' | 'youtube',
    stats?: IApplicationReviewPlatformStats,
): string | null {
    if (!stats) return null;
    if (stats.profile_link) return stats.profile_link;
    const username = (stats.username || '').replace(/^@/, '').trim();
    if (!username) return null;
    if (platform === 'instagram') return `https://instagram.com/${username}`;
    if (platform === 'tiktok') return `https://www.tiktok.com/@${username}`;
    return `https://www.youtube.com/@${username}`;
}

function socialLinksFromInfluencer(infl?: IInfluencerReviewSnapshot) {
    if (!infl) return [];
    const entries: {
        key: 'instagram' | 'tiktok' | 'youtube';
        href: string;
        label: string;
        Glyph: typeof InstagramGlyph;
        color: string;
    }[] = [];
    const ig = platformProfileUrl('instagram', infl.instagram);
    if (ig) {
        entries.push({
            key: 'instagram',
            href: ig,
            label: 'Instagram',
            Glyph: InstagramGlyph,
            color: PLATFORM_BRAND.instagram.primary,
        });
    }
    const tt = platformProfileUrl('tiktok', infl.tiktok);
    if (tt) {
        entries.push({
            key: 'tiktok',
            href: tt,
            label: 'TikTok',
            Glyph: TiktokGlyph,
            color: PLATFORM_BRAND.tiktok.primary,
        });
    }
    const yt = platformProfileUrl('youtube', infl.youtube);
    if (yt) {
        entries.push({
            key: 'youtube',
            href: yt,
            label: 'YouTube',
            Glyph: YoutubeGlyph,
            color: PLATFORM_BRAND.youtube.primary,
        });
    }
    return entries;
}

function followerCountFromInfluencer(infl?: IInfluencerReviewSnapshot): number | null {
    if (!infl) return null;
    const nums = [infl.instagram, infl.tiktok, infl.youtube, infl.facebook, infl.twitter]
        .map(p => p?.followers)
        .filter((n): n is number => typeof n === 'number' && n > 0);
    const maxPlat = nums.length ? Math.max(...nums) : 0;
    const reach = typeof infl.totalReach === 'number' && infl.totalReach > 0 ? infl.totalReach : 0;
    const m = Math.max(maxPlat, reach);
    return m > 0 ? m : null;
}

export interface ApplicationReviewDrawerProps {
    open: boolean;
    onClose: () => void;
    applicant: ICampaignParticipantDetail | null;
    campaignId: string;
    /** Host reviewing a pending invite — Cancel only (no approve/reject). */
    inviteMode?: boolean;
}

export function ApplicationReviewDrawer({
    open,
    onClose,
    applicant,
    campaignId,
    inviteMode = false,
}: ApplicationReviewDrawerProps) {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const dispatch = useDispatch();
    const approving = useSelector((s: RootState) => s.buzzbridge.approvingRequest);
    const rejecting = useSelector((s: RootState) => s.buzzbridge.rejectingRequest);
    const cancellingInvite = useSelector(cancellingInviteSelector);
    const review = useSelector(campaignApplicationReviewSelector);
    const gettingReview = useSelector(gettingCampaignApplicationReviewSelector);
    const reviewError = useSelector(campaignApplicationReviewErrorSelector);

    const [step, setStep] = useState<'review' | 'reject'>('review');
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [comments, setComments] = useState('');
    const [sendEmailCopy, setSendEmailCopy] = useState(true);

    const infl = review?.campaign_application?.influencer;
    const creatorProfile = review?.content_creator_profile ?? null;
    const pastWorkSorted = useMemo(() => {
        const items = review?.past_work ?? [];
        return [...items].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }, [review?.past_work]);

    const displayName = useMemo(() => {
        if (!applicant) return '';
        const n = infl?.name?.trim();
        return n || applicant.name;
    }, [applicant, infl?.name]);

    const socialLinks = useMemo(() => socialLinksFromInfluencer(infl), [infl]);

    const displayAvatar = useMemo(() => {
        if (!applicant) return '';
        return infl?.profile_picture || applicant.avatar;
    }, [applicant, infl?.profile_picture]);

    const displayHandle = useMemo(() => {
        if (!applicant) return '';
        return primaryHandleFromInfluencer(infl) || applicant.handle;
    }, [applicant, infl]);

    const displayFollowers = useMemo(() => {
        if (!applicant) return 0;
        return followerCountFromInfluencer(infl) ?? applicant.followers;
    }, [applicant, infl]);

    const displayEngagement = useMemo(() => {
        if (!applicant) return null as number | null;
        const v = infl?.avg_engagement_rate;
        if (typeof v === 'number') return v;
        return applicant.engagementRate;
    }, [applicant, infl?.avg_engagement_rate]);

    const displayTags = useMemo(() => {
        if (!applicant) return [] as ICampaignParticipantDetail['tags'];
        const cats = infl?.categories;
        if (cats && cats.length > 0) return cats;
        return applicant.tags;
    }, [applicant, infl?.categories]);

    const refetchCampaign = useCallback(() => {
        dispatch(getOffsiteCampaignDetails({ campaignId, asBrandHost: true }));
    }, [campaignId, dispatch]);

    useEffect(() => {
        if (!open || !applicant) return;
        setStep('review');
        setSelectedReasons([]);
        setComments('');
        setSendEmailCopy(true);
    }, [open, applicant]);

    useEffect(() => {
        if (!open || !applicant) return;
        dispatch(getCampaignApplicationReview({ participantId: applicant._id }));
    }, [open, applicant, dispatch]);

    const toggleReason = (label: string) => {
        setSelectedReasons(prev => (prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]));
    };

    const handleClose = () => {
        if (approving || rejecting) return;
        dispatch(clearCampaignApplicationReview());
        onClose();
    };

    const handleRetryReview = () => {
        if (!applicant) return;
        dispatch(getCampaignApplicationReview({ participantId: applicant._id }));
    };

    const handleAccept = () => {
        if (!applicant || approving || inviteMode) return;
        dispatch(
            approveCampaignJoinRequest({
                requestId: applicant._id,
                campaignId,
            }),
        );
        window.setTimeout(refetchCampaign, 500);
        onClose();
    };

    const handleCancelInvite = () => {
        if (!applicant || cancellingInvite) return;
        dispatch(cancelCampaignInvite({ participantId: applicant._id, campaignId }));
        window.setTimeout(refetchCampaign, 500);
        onClose();
    };

    const handleConfirmReject = () => {
        if (!applicant || rejecting || inviteMode) return;
        const parts: string[] = [];
        if (selectedReasons.length) parts.push(`Quick reasons: ${selectedReasons.join(', ')}`);
        if (comments.trim()) parts.push(comments.trim());
        if (sendEmailCopy) parts.push('Brand requested email notification to creator.');
        const reason = parts.join('\n\n') || undefined;
        dispatch(
            rejectCampaignJoinRequest({
                requestId: applicant._id,
                campaignId,
                reason,
            }),
        );
        window.setTimeout(refetchCampaign, 500);
        onClose();
    };

    const handleViewFullProfile = () => {
        if (!applicant) return;
        const userName = applicant?.handle?.startsWith('@')
            ? applicant.handle.slice(1)
            : applicant.handle || displayHandle || '';
        const url = ROUTES.PUBLIC_CREATOR_PROFILE.replace(':userName', `@${userName}`);
        window.open(url, '_blank', 'noopener,noreferrer');
        handleClose();
    };

    if (!applicant) return null;

    const busy = approving || rejecting || cancellingInvite;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            ModalProps={{
                keepMounted: false,
                BackdropProps: {
                    sx: {
                        bgcolor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(10px)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    width: isMdUp ? 'min(100%, 36rem)' : '100%',
                    maxWidth: isMdUp ? 576 : '100%',
                    height: '100%',
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    borderLeft: isMdUp ? `1px solid ${BRAND_OUTLINE_VARIANT}33` : 'none',
                    boxShadow: isMdUp ? '0 -10px 40px rgba(0,0,0,0.8)' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {step === 'review' ? (
                <>
                    <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3, sm: 5 }, pb: 2, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 22, sm: 28 },
                                    fontWeight: 700,
                                    color: BRAND_LUXE_PRIMARY,
                                    letterSpacing: '-0.02em',
                                    pr: 2,
                                }}>
                                Review Application
                            </Typography>
                            <IconButton
                                onClick={handleClose}
                                disabled={busy}
                                aria-label="Close"
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: '#fff',
                                    '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER },
                                }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                border: `1px solid rgba(255,255,255,0.06)`,
                            }}>
                            <Avatar
                                src={displayAvatar || undefined}
                                sx={{
                                    width: 72,
                                    height: 72,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: BRAND_LUXE_PRIMARY,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 24,
                                    ...influencerProfileAvatarSx,
                                }}>
                                {displayName
                                    .split(/\s+/)
                                    .map(w => w[0])
                                    .join('')
                                    .slice(0, 2)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    }}>
                                    <Typography
                                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff' }}
                                        noWrap>
                                        {displayName}
                                    </Typography>
                                    {socialLinks.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                            {socialLinks.map(({ key, href, label, Glyph, color }) => (
                                                <IconButton
                                                    key={key}
                                                    component="a"
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Open ${label}`}
                                                    size="small"
                                                    sx={{
                                                        color,
                                                        bgcolor: 'rgba(255,255,255,0.06)',
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                                                    }}>
                                                    <Glyph sx={{ width: 16, height: 16 }} color={color} />
                                                </IconButton>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: ON_VARIANT }}>
                                    {displayHandle}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: { xs: 3, sm: 4 },
                            pb: 2,
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                borderRadius: 2,
                            },
                        }}>
                        {reviewError ? (
                            <Alert
                                severity="error"
                                action={
                                    <Button color="inherit" size="small" onClick={handleRetryReview}>
                                        Retry
                                    </Button>
                                }
                                sx={{
                                    mb: 2,
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: '#fff',
                                    '& .MuiAlert-icon': { color: '#f87171' },
                                }}>
                                Could not load full application details. You can still accept or reject using the
                                summary above.
                            </Alert>
                        ) : null}

                        {gettingReview && !review ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={36} sx={{ color: BRAND_LUXE_PRIMARY }} />
                            </Box>
                        ) : null}

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                            <StatPill label="Followers" value={formatFollowers(displayFollowers)} />
                            <StatPill
                                label="Eng. rate"
                                value={displayEngagement != null ? `${displayEngagement}%` : '—'}
                            />
                        </Box>

                        {displayTags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                                {displayTags.map((t, i) => (
                                    <Chip
                                        key={i}
                                        label={tagLabel(t)}
                                        size="small"
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                            height: 24,
                                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                            color: '#fff',
                                        }}
                                    />
                                ))}
                            </Box>
                        )}

                        {applicant.pitch ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: ON_VARIANT,
                                    lineHeight: 1.6,
                                    fontStyle: 'italic',
                                    mb: infl?.bio ? 2 : 0,
                                }}>
                                &ldquo;{applicant.pitch}&rdquo;
                            </Typography>
                        ) : null}

                        {infl?.bio ? (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.2em',
                                        textTransform: 'uppercase',
                                        mb: 1,
                                    }}>
                                    Bio
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                        color: ON_VARIANT,
                                        lineHeight: 1.6,
                                        whiteSpace: 'pre-wrap',
                                    }}>
                                    {infl.bio}
                                </Typography>
                            </Box>
                        ) : null}

                        {creatorProfile ? (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.2em',
                                        textTransform: 'uppercase',
                                        mb: 1,
                                    }}>
                                    Creator profile
                                </Typography>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: '#fff', mb: 0.5 }}>
                                    {[creatorProfile.stage_name, creatorProfile.title].filter(Boolean).join(' · ') ||
                                        'Content creator'}
                                </Typography>
                                {typeof creatorProfile.about === 'string' && creatorProfile.about.trim() ? (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 13,
                                            color: ON_VARIANT,
                                            lineHeight: 1.55,
                                            whiteSpace: 'pre-wrap',
                                        }}>
                                        {creatorProfile.about}
                                    </Typography>
                                ) : null}
                                {creatorProfile.links && creatorProfile.links.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                                        {creatorProfile.links.map((item, idx) =>
                                            item.link ? (
                                                <Link
                                                    key={idx}
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 12,
                                                        color: BRAND_LUXE_PRIMARY,
                                                    }}>
                                                    {item.description?.trim() || item.link}
                                                </Link>
                                            ) : null,
                                        )}
                                    </Box>
                                ) : null}
                            </Box>
                        ) : null}

                        {pastWorkSorted.length > 0 ? (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 11,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.2em',
                                        textTransform: 'uppercase',
                                        mb: 1,
                                    }}>
                                    Past work
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {pastWorkSorted.map((w, idx) => (
                                        <Box
                                            key={w._id ?? `${w.title}-${idx}`}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                                border: '1px solid rgba(255,255,255,0.06)',
                                            }}>
                                            <Typography
                                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                                                {w.title}
                                            </Typography>
                                            {w.year ? (
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: 11,
                                                        color: ON_VARIANT,
                                                        mt: 0.25,
                                                    }}>
                                                    {w.year}
                                                    {w.type ? ` · ${w.type}` : ''}
                                                </Typography>
                                            ) : null}
                                            {w.description ? (
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: 13,
                                                        color: ON_VARIANT,
                                                        mt: 0.75,
                                                        lineHeight: 1.5,
                                                    }}>
                                                    {w.description}
                                                </Typography>
                                            ) : null}
                                            {w.link ? (
                                                <Link
                                                    href={w.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'inline-block',
                                                        mt: 1,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 12,
                                                        color: BRAND_LUXE_PRIMARY,
                                                    }}>
                                                    Open link
                                                </Link>
                                            ) : null}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ) : null}

                        <Button
                            onClick={handleViewFullProfile}
                            sx={{
                                mt: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: BRAND_LUXE_PRIMARY,
                                p: 0,
                                minWidth: 0,
                                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                            }}>
                            View full profile
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            p: { xs: 3, sm: 4 },
                            pt: 2,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            borderTop: `1px solid rgba(255,255,255,0.06)`,
                        }}>
                        {inviteMode ? (
                            <Button
                                fullWidth
                                variant="outlined"
                                disabled={busy}
                                onClick={handleCancelInvite}
                                sx={{
                                    py: 1.75,
                                    borderRadius: 999,
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 12,
                                    letterSpacing: '0.06em',
                                    borderColor: 'rgba(255,107,107,0.45)',
                                    color: '#ff8a80',
                                    '&:hover': {
                                        borderColor: 'rgba(255,107,107,0.7)',
                                        bgcolor: 'rgba(255,107,107,0.08)',
                                    },
                                }}>
                                {cancellingInvite ? (
                                    <CircularProgress size={18} thickness={5} sx={{ color: '#ff8a80' }} />
                                ) : (
                                    'Cancel invite'
                                )}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    fullWidth
                                    disabled={busy}
                                    onClick={handleAccept}
                                    sx={{
                                        py: 1.75,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        letterSpacing: '0.08em',
                                        bgcolor: BRAND_LUXE_PRIMARY,
                                        color: '#392700',
                                        boxShadow: '0 8px 24px rgba(255,201,101,0.2)',
                                        '&:hover': { bgcolor: '#feb700' },
                                    }}>
                                    Accept
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    disabled={busy}
                                    onClick={() => setStep('reject')}
                                    sx={{
                                        py: 1.75,
                                        borderRadius: 999,
                                        textTransform: 'uppercase',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        letterSpacing: '0.06em',
                                        borderColor: BRAND_OUTLINE_VARIANT,
                                        color: '#fff',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.35)',
                                            bgcolor: 'rgba(255,255,255,0.04)',
                                        },
                                    }}>
                                    Reject
                                </Button>
                            </>
                        )}
                    </Box>
                </>
            ) : (
                <>
                    <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3, sm: 5 }, pb: 2, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 22, sm: 28 },
                                    fontWeight: 700,
                                    color: BRAND_LUXE_PRIMARY,
                                    letterSpacing: '-0.02em',
                                    pr: 2,
                                }}>
                                Leave Feedback
                            </Typography>
                            <IconButton
                                onClick={handleClose}
                                disabled={busy}
                                aria-label="Close"
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    color: '#fff',
                                    '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER },
                                }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                borderLeft: `4px solid rgba(255,201,101,0.45)`,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 16,
                                    color: '#fff',
                                    lineHeight: 1.6,
                                }}>
                                Tell{' '}
                                <Box component="span" sx={{ fontWeight: 700, color: BRAND_LUXE_PRIMARY }}>
                                    {displayName}
                                </Box>{' '}
                                why their application wasn&apos;t the right fit for this campaign.
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: { xs: 3, sm: 4 },
                            pb: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                borderRadius: 2,
                            },
                        }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 11,
                                    color: ON_VARIANT,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    mb: 2,
                                }}>
                                Quick Reasons
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                {QUICK_REASONS.map(label => {
                                    const on = selectedReasons.includes(label);
                                    return (
                                        <Button
                                            key={label}
                                            onClick={() => toggleReason(label)}
                                            sx={{
                                                px: 2.5,
                                                py: 1,
                                                borderRadius: 999,
                                                textTransform: 'none',
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 13,
                                                border: on
                                                    ? `1px solid rgba(255,201,101,0.45)`
                                                    : `1px solid rgba(72,72,71,0.35)`,
                                                bgcolor: on ? 'rgba(255,201,101,0.1)' : BRAND_SURFACE_CONTAINER_HIGHEST,
                                                color: on ? BRAND_LUXE_PRIMARY : ON_VARIANT,
                                                '&:hover': {
                                                    borderColor: 'rgba(255,201,101,0.35)',
                                                    bgcolor: on ? 'rgba(255,201,101,0.14)' : 'rgba(255,201,101,0.05)',
                                                },
                                            }}>
                                            {label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 200 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 11,
                                    color: ON_VARIANT,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    mb: 2,
                                }}>
                                Detailed Comments
                            </Typography>
                            <TextField
                                multiline
                                fullWidth
                                minRows={8}
                                placeholder="Provide specific details to help the creator improve..."
                                value={comments}
                                onChange={e => setComments(e.target.value)}
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        borderRadius: 3,
                                        color: '#fff',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        alignItems: 'flex-start',
                                        '& fieldset': { border: 'none' },
                                        '&:hover fieldset': { border: 'none' },
                                        '&.Mui-focused fieldset': {
                                            border: '1px solid rgba(236,170,0,0.35)',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                borderRadius: 2,
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                gap: 2,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                <MailOutlineIcon sx={{ color: BRAND_LUXE_SECONDARY, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }}>
                                    Send copy to creator via email
                                </Typography>
                            </Box>
                            <Switch
                                checked={sendEmailCopy}
                                onChange={(_, v) => setSendEmailCopy(v)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_SECONDARY },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        bgcolor: `${BRAND_LUXE_SECONDARY}88`,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            p: { xs: 3, sm: 4 },
                            pt: 2,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            borderTop: `1px solid rgba(255,255,255,0.06)`,
                        }}>
                        <Button
                            fullWidth
                            disabled={busy}
                            onClick={handleConfirmReject}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                py: 1.75,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 16,
                                textTransform: 'none',
                                background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                                color: '#392700',
                                boxShadow: '0 8px 24px rgba(255,201,101,0.15)',
                                '&:hover': { background: `linear-gradient(90deg, #ffd080, #feb700)` },
                            }}>
                            Confirm Rejection
                        </Button>
                        <Button
                            fullWidth
                            disabled={busy}
                            onClick={() => setStep('review')}
                            sx={{
                                py: 1.75,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 15,
                                border: `1px solid rgba(72,72,71,0.35)`,
                                color: ON_VARIANT,
                                '&:hover': { color: '#fff', bgcolor: BRAND_SURFACE_CONTAINER },
                            }}>
                            Cancel
                        </Button>
                    </Box>
                </>
            )}
        </Drawer>
    );
}

function StatPill({ label, value }: { label: string; value: string }) {
    return (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST }}>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 10, color: ON_VARIANT, mb: 0.25 }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff' }}>{value}</Typography>
        </Box>
    );
}
